import * as React from "react";

import { Text } from "../display/Text";
import { useControlled } from "../forms/useControlled";
import { useFormFieldProps } from "../salt/form-field-context";
import {
  calculateMarkPosition,
  calculatePercentage,
  clamp,
  clampRange,
  getClickedPosition,
  getKeyboardValue,
  toFloat,
  type SliderMark,
} from "./slider-utils";
import "./slider.css";

/**
 * Slider and RangeSlider, replacing `@salt-ds/core`'s behind `slider.tsx` (5 call sites).
 *
 * ── THE THUMB IS A REAL <input type="range">, AND THAT IS THE POINT ───────────────────────
 *
 * Everything visible — the rail, the fill, the round thumb, the tooltip — is decoration drawn
 * on top of a native range input that is present, focusable, and carries the ARIA. That is not
 * belt-and-braces: a div rebuilding a slider has to reimplement focus, every key, the value
 * announcement, and forms integration, and screen readers still treat it as a custom widget.
 * Keeping the real input means the accessibility works because it is the platform's, not
 * because we remembered every case.
 *
 * The input is visually hidden but NOT `display: none` — a hidden-that-way input cannot be
 * focused, which would defeat the entire arrangement.
 *
 * ── WHY KEY HANDLING IS NOT LEFT TO THE INPUT ─────────────────────────────────────────────
 *
 * A native range input already moves on arrow keys, but only by `step`. Home/End/PageUp/PageDown
 * and mark-to-mark stepping are ours, so keys are computed in `getKeyboardValue`, which calls
 * `preventDefault` ONLY for keys it handled. That is what stops a handled key being applied
 * twice — once by us and once by the browser — while leaving Tab alone.
 *
 * ── A VENDOR QUIRK THIS PORT KEEPS ────────────────────────────────────────────────────────
 *
 * Keyboard changes reach `onChange` with `undefined` as the event argument. The key handler
 * synthesises `{ target: { value } }` rather than a real event, and the change handler passes
 * `event.nativeEvent` on — which does not exist on that object. Callers already receive
 * `undefined` there today, so "fixing" it would hand them an event they have never had and may
 * not expect. The value argument, which is the one anybody uses, is correct on both paths.
 */

export interface SliderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange" | "defaultValue"> {
  accessibleMaxText?: string;
  accessibleMinText?: string;
  constrainLabelPosition?: boolean;
  decimalPlaces?: number;
  defaultValue?: number;
  disabled?: boolean;
  format?: (value: number) => string | number;
  marks?: SliderMark[];
  max?: number;
  min?: number;
  maxLabel?: string;
  minLabel?: string;
  onChange?: (event: Event, value: number) => void;
  onChangeEnd?: (event: Event, value: number) => void;
  restrictToMarks?: boolean;
  showTicks?: boolean;
  showTooltip?: boolean;
  step?: number;
  stepMultiplier?: number;
  value?: number;
}

export interface RangeSliderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange" | "defaultValue"> {
  accessibleMaxText?: string;
  accessibleMinText?: string;
  constrainLabelPosition?: boolean;
  decimalPlaces?: number;
  defaultValue?: [number, number];
  disabled?: boolean;
  format?: (value: number) => string | number;
  marks?: SliderMark[];
  max?: number;
  min?: number;
  maxLabel?: string;
  minLabel?: string;
  onChange?: (event: Event, value: [number, number]) => void;
  onChangeEnd?: (event: Event, value: [number, number]) => void;
  restrictToMarks?: boolean;
  showTicks?: boolean;
  showTooltip?: boolean;
  step?: number;
  stepMultiplier?: number;
  value?: [number, number];
}

/** A change carrying only what the handlers read, so a key press can raise one without a DOM event. */
type ValueChange = { target: { value: string }; nativeEvent?: Event };

// ─────────────────────────────────────────────────────────────────────────────────────────
// Tooltip
// ─────────────────────────────────────────────────────────────────────────────────────────

function SliderTooltip({ open, value }: { open: boolean; value: React.ReactNode }) {
  return (
    <div
      aria-hidden
      className={`saltSliderTooltip${open ? " saltSliderTooltip-visible" : ""}`}
      data-testid="sliderTooltip"
    >
      {/*
        Two paths on the same coordinates: the first paints the tooltip's own background over
        whatever sits behind the arrow, the second draws its border on top. One path cannot do
        both, because a single stroke would be painted over by the fill it shares an edge with.
      */}
      <svg aria-hidden="true" className="saltSliderTooltip-arrow" viewBox="0 1 14 14">
        <path d="M0,0 H14 L7,7 Q7,7 7,7 Z" stroke="var(--salt-container-primary-background)" />
        <path d="M0,0 H14 L7,7 Q7,7 7,7 Z" stroke="var(--salt-container-primary-borderColor)" />
      </svg>
      <Text className="saltSliderTooltip-text">{value}</Text>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// Thumb
// ─────────────────────────────────────────────────────────────────────────────────────────

interface SliderThumbProps {
  "aria-label"?: string;
  "aria-labelledby"?: string;
  /**
   * Redundant against the input's own `min`/`max` — a native range input already exposes both
   * — but part of the rendered contract, and an assistive technology that reads the explicit
   * attribute in preference to the implicit one loses nothing by their being present.
   */
  "aria-valuemax"?: number;
  "aria-valuemin"?: number;
  "aria-valuetext"?: string;
  accessibleMaxText?: string;
  accessibleMinText?: string;
  disabled?: boolean;
  format?: (value: number) => string | number;
  handleInputChange: (event: ValueChange) => void;
  handleKeydownOnThumb: (event: React.KeyboardEvent) => void;
  handlePointerDown: (event: React.PointerEvent) => void;
  index?: number;
  inputRef: React.RefObject<HTMLInputElement>;
  isFocusVisible: boolean;
  max: number;
  min: number;
  offsetPercentage: string;
  onBlur?: () => void;
  onFocus?: () => void;
  restrictToMarks?: boolean;
  showTooltip?: boolean;
  sliderValue: number | [number, number];
  step: number;
  trackDragging: boolean;
}

function SliderThumb({
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "aria-valuemax": ariaValueMax,
  "aria-valuemin": ariaValueMin,
  "aria-valuetext": ariaValueText,
  accessibleMaxText,
  accessibleMinText,
  disabled,
  format,
  handleInputChange,
  handleKeydownOnThumb,
  handlePointerDown,
  index = 0,
  inputRef,
  isFocusVisible,
  max,
  min,
  offsetPercentage,
  onBlur,
  onFocus,
  restrictToMarks,
  showTooltip,
  sliderValue,
  step,
  trackDragging,
}: SliderThumbProps) {
  const [isTooltipVisible, setIsTooltipVisible] = React.useState(false);
  const reactId = React.useId();
  const accessibleTextId = `saltSlider-${reactId}-${index}`;
  const value = Array.isArray(sliderValue) ? sliderValue[index] : sliderValue;
  const formattedValue = format ? format(value) : value;

  React.useEffect(() => {
    if (!showTooltip || !isTooltipVisible) return undefined;
    // Escape dismisses the tooltip, and the listener is on the WINDOW because the pointer may
    // have left the thumb while the tooltip is still up — a thumb-scoped listener would never
    // hear the key that is meant to dismiss it.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsTooltipVisible(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isTooltipVisible, showTooltip]);

  const classes = ["saltSliderThumb"];
  if (isFocusVisible) classes.push("saltSliderThumb-focusVisible");
  if (disabled) classes.push("saltSliderThumb-disabled");
  if (trackDragging) classes.push("saltSliderThumb-dragging");
  if (index === 1) classes.push("saltSliderThumb-secondThumb");

  const hoverProps = showTooltip
    ? {
        onPointerEnter: () => setIsTooltipVisible(true),
        // Delayed so the tooltip does not vanish while the pointer crosses the gap between the
        // thumb and the tooltip itself.
        onPointerLeave: () => {
          setTimeout(() => setIsTooltipVisible(false), 300);
        },
      }
    : {};

  return (
    <div
      className={classes.join(" ")}
      data-testid="sliderThumb"
      onPointerDown={handlePointerDown}
      style={{ left: offsetPercentage }}
      {...hoverProps}
    >
      {showTooltip && (
        <SliderTooltip
          open={(isTooltipVisible || trackDragging || isFocusVisible) && !disabled}
          value={formattedValue}
        />
      )}
      <input
        aria-describedby={accessibleTextId}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-valuemax={ariaValueMax}
        aria-valuemin={ariaValueMin}
        aria-valuenow={value}
        aria-valuetext={ariaValueText || format?.(value).toString()}
        className="saltSliderThumb-input"
        disabled={disabled}
        max={max}
        min={min}
        onBlur={onBlur}
        onChange={handleInputChange}
        onFocus={onFocus}
        onKeyDown={handleKeydownOnThumb}
        ref={inputRef}
        step={step}
        type="range"
        value={value}
      />
      {/*
        The bounds spoken once, on focus, via aria-describedby — NOT repeated in aria-valuetext,
        which is re-announced on every single value change. Hearing "minimum 0, maximum 100"
        after each arrow key is what makes a slider unusable with a screen reader.
      */}
      <span aria-hidden="true" className="saltSliderThumb-accessibleText" id={accessibleTextId}>
        {Array.isArray(sliderValue) &&
          `${index === 0 ? "leading" : "trailing"}, ${format?.(sliderValue[0]) || sliderValue[0]} to ${
            format?.(sliderValue[1]) || sliderValue[1]
          }, `}
        {"range "}
        {accessibleMinText ? `${accessibleMinText} ${min}, ` : `minimum ${format?.(min) || min}, `}
        {accessibleMaxText ? `${accessibleMaxText} ${max} ` : `maximum ${format?.(max) || max}`}
        {restrictToMarks ? ", custom increments" : step !== 1 && `, increments of ${step}`}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// Track
// ─────────────────────────────────────────────────────────────────────────────────────────

interface SliderTrackProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  children: React.ReactNode;
  constrainLabelPosition?: boolean;
  disabled: boolean;
  format?: (value: number) => string | number;
  handlePointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
  isDragging: boolean;
  isRange?: boolean;
  marks?: SliderMark[];
  max: number;
  maxLabel?: string;
  min: number;
  minLabel?: string;
  progressPercentage?: number;
  progressPercentageRange?: [number, number];
  showTicks?: boolean;
  sliderRef: React.RefObject<HTMLDivElement>;
}

const SliderTrack = React.forwardRef<HTMLDivElement, SliderTrackProps>(function SliderTrack(
  {
    children,
    className,
    constrainLabelPosition = false,
    disabled,
    format,
    handlePointerDown,
    isDragging,
    isRange = false,
    marks,
    max,
    maxLabel,
    min,
    minLabel,
    progressPercentage = 0,
    progressPercentageRange = [0, 0],
    showTicks,
    sliderRef,
    ...rest
  },
  ref,
) {
  const isMarkSelected = (value: number) => {
    const percentage = calculatePercentage(value, max, min);
    return isRange
      ? percentage > progressPercentageRange[0] && percentage < progressPercentageRange[1]
      : percentage < progressPercentage;
  };
  // A mark sitting exactly under a thumb is HIDDEN rather than drawn: the thumb covers it, and a
  // tick peeking out from under the thumb reads as a rendering fault.
  const isMarkOverlapped = (value: number) => {
    const percentage = calculatePercentage(value, max, min);
    return isRange
      ? percentage === progressPercentageRange[0] || percentage === progressPercentageRange[1]
      : percentage === progressPercentage;
  };

  const classes = ["saltSliderTrack"];
  if (className) classes.push(className);
  if (disabled) classes.push("saltSliderTrack-disabled");
  if (isDragging) classes.push("saltSliderTrack-dragging");
  if (isRange) classes.push("saltSliderTrack-range");
  if (marks) classes.push("saltSliderTrack-withMarks");
  if (constrainLabelPosition) classes.push("saltSliderTrack-constrainLabelPosition");
  if (showTicks) classes.push("saltSliderTrack-withTicks");

  const railClasses = ["saltSliderTrack-rail"];
  if (showTicks && marks?.some((mark) => mark.value === min)) {
    railClasses.push("saltSliderTrack-hasMinTick");
  }
  if (showTicks && marks?.some((mark) => mark.value === max)) {
    railClasses.push("saltSliderTrack-hasMaxTick");
  }

  return (
    <div className={classes.join(" ")} data-testid="sliderTrack" ref={ref} {...rest}>
      <div className="saltSliderTrack-container">
        {minLabel && (
          <Text aria-hidden className="saltSliderTrack-minLabel" color="secondary" styleAs="label">
            {minLabel || format?.(min)}
          </Text>
        )}
        <div className="saltSliderTrack-wrapper" onPointerDown={handlePointerDown}>
          <div
            className={railClasses.join(" ")}
            ref={sliderRef}
            style={
              {
                "--slider-progressPercentage": `${progressPercentage}%`,
                "--slider-progressPercentageStart": `${progressPercentageRange[0]}%`,
                "--slider-progressPercentageEnd": `${progressPercentageRange[1]}%`,
              } as React.CSSProperties
            }
          >
            {isRange && <div className="saltSliderTrack-fill" />}
            {children}
          </div>
          {marks && showTicks && (
            <div className="saltSliderTrack-ticks">
              {marks.map(({ value }) => {
                const tickClasses = ["saltSliderTrack-tick"];
                if (isMarkSelected(value)) tickClasses.push("saltSliderTrack-tickSelected");
                if (isMarkOverlapped(value)) tickClasses.push("saltSliderTrack-tickHidden");
                return (
                  <span
                    className={tickClasses.join(" ")}
                    key={`${value}-tick`}
                    style={{ left: `${calculateMarkPosition(value, max, min)}%` }}
                  />
                );
              })}
            </div>
          )}
          {marks && (
            <div className="saltSliderTrack-marks">
              {marks.map(({ label, value }) => (
                <span
                  className="saltSliderTrack-markLabel"
                  data-testid="mark"
                  key={`${value}-mark`}
                  style={{ left: `${calculateMarkPosition(value, max, min)}%` }}
                >
                  {label}
                </span>
              ))}
            </div>
          )}
        </div>
        {maxLabel && (
          <Text aria-hidden className="saltSliderTrack-maxLabel" color="secondary" styleAs="label">
            {maxLabel || format?.(max)}
          </Text>
        )}
      </div>
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────────────────────
// Drag plumbing, shared by both sliders
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * Attach the drag listeners to the WINDOW, not the thumb.
 *
 * A pointer that leaves the thumb mid-drag must keep dragging — that is what dragging IS. Bound
 * to the thumb, the slider would stop the instant the pointer strayed a few pixels off it. The
 * same reason explains `blur` and `contextmenu` in the end list: a drag interrupted by an alt-tab
 * or a right-click otherwise never ends, and the slider follows the pointer forever afterwards.
 */
function useDragListeners(
  isDragging: boolean,
  onMove: (event: PointerEvent) => void,
  onEnd: (event: Event) => void,
) {
  React.useEffect(() => {
    if (!isDragging) return undefined;
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onEnd);
    window.addEventListener("pointercancel", onEnd);
    window.addEventListener("blur", onEnd);
    window.addEventListener("contextmenu", onEnd);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onEnd);
      window.removeEventListener("pointercancel", onEnd);
      window.removeEventListener("blur", onEnd);
      window.removeEventListener("contextmenu", onEnd);
    };
  }, [isDragging, onMove, onEnd]);
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// Slider
// ─────────────────────────────────────────────────────────────────────────────────────────

export const Slider = React.forwardRef<HTMLDivElement, SliderProps>(function Slider(
  {
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-valuetext": ariaValueText,
    accessibleMaxText,
    accessibleMinText,
    decimalPlaces = 2,
    disabled: disabledProp = false,
    format,
    marks,
    min = 0,
    minLabel,
    max = 100,
    maxLabel,
    onChange,
    onChangeEnd,
    restrictToMarks = false,
    showTooltip = true,
    step = 1,
    stepMultiplier = 2,
    value: valueProp,
    defaultValue = min + (max - min) / 2,
    ...rest
  },
  ref,
) {
  const [valueState, setValue] = useControlled({
    controlled: valueProp,
    default: defaultValue,
  });
  const { a11yProps, disabled: formFieldDisabled } = useFormFieldProps();
  const disabled = formFieldDisabled || disabledProp;
  const inputRef = React.useRef<HTMLInputElement>(null);
  const sliderRef = React.useRef<HTMLDivElement>(null);

  // Clamped on every render, not on write: a controlled caller can hand us anything at any time,
  // and the thumb must never be drawn off the end of its own track.
  const value = clamp(valueState, max, min, step, decimalPlaces, marks, restrictToMarks);
  const progressPercentage = calculatePercentage(toFloat(value), max, min);

  const [isDragging, setIsDragging] = React.useState(false);
  const [isFocusVisible, setIsFocusVisible] = React.useState(false);
  /*
   * TWO de-duplicators, not one, and they are refs rather than state on purpose: a drag fires
   * pointermove far faster than React re-renders, so comparing against the rendered value would
   * let the same value through repeatedly and fire `onChange` once per frame.
   *
   * They are SEPARATE because they guard different paths. `lastGestureRef` remembers the last
   * value a gesture (drag or key press) computed; `lastCommittedRef` remembers the last value
   * actually delivered to the caller. Collapsing them into one ref looks like a tidy-up and
   * silently breaks the keyboard: the key handler records its new value, then calls the change
   * handler, which sees its own ref already holding that value and returns without firing
   * anything. The slider then moves visually and reports nothing.
   */
  const lastGestureRef = React.useRef(value);
  const lastCommittedRef = React.useRef(value);

  const handleInputChange = (event: ValueChange) => {
    const parsed = toFloat(event.target.value);
    if (parsed === lastCommittedRef.current) return;
    setValue(parsed);
    onChange?.(event.nativeEvent as Event, parsed);
    onChangeEnd?.(event.nativeEvent as Event, parsed);
    lastCommittedRef.current = parsed;
  };

  const handlePointerMove = React.useCallback(
    (event: PointerEvent) => {
      const newValue = getClickedPosition(
        sliderRef, event.clientX, max, min, step, decimalPlaces, marks, restrictToMarks,
      );
      if (newValue === undefined || lastGestureRef.current === newValue) return;
      lastGestureRef.current = newValue;
      setValue(newValue);
      onChange?.(event, newValue);
    },
    [decimalPlaces, marks, max, min, onChange, restrictToMarks, setValue, step],
  );

  const handleDragEnd = React.useCallback(
    (event: Event) => {
      setIsDragging(false);
      setIsFocusVisible(false);
      // The ref, not the rendered value: the last pointermove of a drag may not have rendered yet.
      onChangeEnd?.(event, lastGestureRef.current);
    },
    [onChangeEnd],
  );

  useDragListeners(isDragging, handlePointerMove, handleDragEnd);

  const handlePointerDownOnTrack = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    inputRef.current?.focus();
    setIsDragging(true);
    // Focus moves to the input on a pointer press, but the focus RING must not appear: it is a
    // keyboard affordance, and drawing it on click is the classic "why is this outlined" bug.
    setIsFocusVisible(false);
    const newValue = getClickedPosition(
      sliderRef, event.clientX, max, min, step, decimalPlaces, marks, restrictToMarks,
    );
    if (newValue === undefined || lastGestureRef.current === newValue) return;
    lastGestureRef.current = newValue;
    setValue(newValue);
    onChange?.(event.nativeEvent, newValue);
  };

  const handlePointerDownOnThumb = (event: React.PointerEvent) => {
    event.preventDefault();
    // Stopped so the track handler does not ALSO run and jump the value to the press position —
    // grabbing a thumb must not move it.
    event.stopPropagation();
    inputRef.current?.focus();
    setIsDragging(true);
    setIsFocusVisible(false);
  };

  const handleKeydownOnThumb = (event: React.KeyboardEvent) => {
    const newValue = getKeyboardValue(
      event, value, step, stepMultiplier, max, min, restrictToMarks, marks,
    );
    if (newValue === lastGestureRef.current) return;
    setIsFocusVisible(true);
    lastGestureRef.current = newValue;
    handleInputChange({ target: { value: newValue.toString() } });
  };

  return (
    <SliderTrack
      disabled={disabled}
      format={format}
      handlePointerDown={handlePointerDownOnTrack}
      isDragging={isDragging}
      marks={marks}
      max={max}
      maxLabel={maxLabel}
      min={min}
      minLabel={minLabel}
      progressPercentage={progressPercentage}
      ref={ref}
      sliderRef={sliderRef}
      {...rest}
    >
      <SliderThumb
        accessibleMaxText={accessibleMaxText}
        accessibleMinText={accessibleMinText}
        aria-label={ariaLabel}
        // The FormField's label and the caller's own, in that order — a slider inside a
        // FormField must still answer to the field's label, or it is an unnamed control.
        aria-labelledby={[a11yProps?.["aria-labelledby"], ariaLabelledBy].filter(Boolean).join(" ") || undefined}
        aria-valuemax={max}
        aria-valuemin={min}
        aria-valuetext={ariaValueText}
        disabled={disabled}
        format={format}
        handleInputChange={handleInputChange}
        handleKeydownOnThumb={handleKeydownOnThumb}
        handlePointerDown={handlePointerDownOnThumb}
        inputRef={inputRef}
        isFocusVisible={isFocusVisible}
        max={max}
        min={min}
        offsetPercentage={`${progressPercentage}%`}
        onBlur={() => setIsFocusVisible(false)}
        onFocus={() => setIsFocusVisible(true)}
        restrictToMarks={restrictToMarks}
        showTooltip={showTooltip}
        sliderValue={value}
        step={step}
        trackDragging={isDragging}
      />
    </SliderTrack>
  );
});

// ─────────────────────────────────────────────────────────────────────────────────────────
// RangeSlider
// ─────────────────────────────────────────────────────────────────────────────────────────

export const RangeSlider = React.forwardRef<HTMLDivElement, RangeSliderProps>(
  function RangeSlider(
    {
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      "aria-valuetext": ariaValueText,
      accessibleMaxText,
      accessibleMinText,
      decimalPlaces = 2,
      disabled: disabledProp = false,
      format,
      marks,
      max = 100,
      min = 0,
      maxLabel,
      minLabel,
      onChange,
      onChangeEnd,
      restrictToMarks = false,
      showTooltip = true,
      step = 1,
      stepMultiplier = 2,
      value: valueProp,
      defaultValue = [min, min + (max - min) / 2],
      ...rest
    },
    ref,
  ) {
    const [valueState, setValue] = useControlled<[number, number]>({
      controlled: valueProp,
      default: defaultValue,
    });
    const { a11yProps, disabled: formFieldDisabled } = useFormFieldProps();
    const disabled = formFieldDisabled || disabledProp;
    const inputRefs = [
      React.useRef<HTMLInputElement>(null),
      React.useRef<HTMLInputElement>(null),
    ];
    const sliderRef = React.useRef<HTMLDivElement>(null);

    const value = clampRange(valueState, max, min, step, decimalPlaces, marks, restrictToMarks);
    const startPercentage = calculatePercentage(value[0], max, min);
    const endPercentage = calculatePercentage(value[1], max, min);

    const [isDragging, setIsDragging] = React.useState(false);
    const [isFocusVisible, setIsFocusVisible] = React.useState(false);
    const [thumbIndexState, setThumbIndex] = React.useState(0);
    /*
     * Same two-ref split as Slider above, and for the same reason — but each gets its OWN COPY
     * of the tuple. Seeding both from `value` hands them one array object, and then a write
     * through either ref is visible through the other: the key handler records its new value
     * and the change handler, reading what looks like an independent ref, sees the value it is
     * about to deliver already sitting there and fires nothing. Two refs that alias the same
     * array are one ref wearing two names.
     */
    const lastGestureRef = React.useRef<[number, number]>([...value]);
    const lastCommittedRef = React.useRef<[number, number]>([...value]);

    /**
     * Two thumbs on one track can cross, and a crossed range is meaningless. Rather than
     * blocking the move, a thumb pushed past its partner is CLAMPED to it: the drag keeps
     * responding and the range simply collapses to zero width, which is a state the user can
     * see and undo. Refusing the move would make the thumb feel stuck for no visible reason.
     */
    const preventThumbOverlap = React.useCallback(
      (current: number, values: [number, number], thumbIndex: number): [number, number] => {
        const next: [number, number] = [...values];
        if (thumbIndex === 0 && current >= next[1]) next[0] = next[1];
        else if (thumbIndex === 1 && current <= next[0]) next[1] = next[0];
        else next[thumbIndex] = current;
        return next;
      },
      [],
    );

    const handleInputChange = (event: ValueChange, thumbIndex: number) => {
      const parsed = toFloat(event.target.value);
      const values = preventThumbOverlap(parsed, value, thumbIndex);
      if (values[0] === lastCommittedRef.current[0] && values[1] === lastCommittedRef.current[1]) return;
      setValue(values);
      onChange?.(event.nativeEvent as Event, values);
      onChangeEnd?.(event.nativeEvent as Event, values);
      lastCommittedRef.current = values;
    };

    const handlePointerMove = React.useCallback(
      (event: PointerEvent) => {
        const newValue = getClickedPosition(
          sliderRef, event.clientX, max, min, step, decimalPlaces, marks, restrictToMarks,
        );
        if (newValue === undefined) return;
        const next = preventThumbOverlap(newValue, value, thumbIndexState);
        if (next[0] === lastGestureRef.current[0] && next[1] === lastGestureRef.current[1]) return;
        lastGestureRef.current = next;
        setValue(next);
        onChange?.(event, next);
      },
      [decimalPlaces, marks, max, min, onChange, preventThumbOverlap, restrictToMarks, setValue, step, thumbIndexState, value],
    );

    const handleDragEnd = React.useCallback(
      (event: Event) => {
        setIsDragging(false);
        setIsFocusVisible(false);
        onChangeEnd?.(event, lastGestureRef.current);
      },
      [onChangeEnd],
    );

    useDragListeners(isDragging, handlePointerMove, handleDragEnd);

    const handlePointerDownOnTrack = (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragging(true);
      const newValue = getClickedPosition(
        sliderRef, event.clientX, max, min, step, decimalPlaces, marks, restrictToMarks,
      );
      if (newValue === undefined) return;

      /*
       * A click on the track moves the NEARER thumb. On an exact tie the tie-break is by
       * direction — outside the range, the thumb on that side moves; inside it, the leading one
       * does. Picking a fixed thumb on a tie would make a click just outside the range sometimes
       * drag the far thumb across the whole track.
       */
      const next: [number, number] = [...value];
      let closest = 0;
      const toStart = Math.abs(newValue - next[0]);
      const toEnd = Math.abs(newValue - next[1]);
      if (toStart > toEnd) {
        next[1] = newValue;
        closest = 1;
      } else if (toStart < toEnd) {
        next[0] = newValue;
        closest = 0;
      } else if (newValue > next[1]) {
        next[1] = newValue;
        closest = 1;
      } else {
        next[0] = newValue;
        closest = 0;
      }

      setThumbIndex(closest);
      inputRefs[closest].current?.focus();
      setIsFocusVisible(false);
      if (next[0] === lastGestureRef.current[0] && next[1] === lastGestureRef.current[1]) return;
      lastGestureRef.current = next;
      setValue(next);
      onChange?.(event.nativeEvent, next);
    };

    const handlePointerDownOnThumb = (event: React.PointerEvent, thumbIndex: number) => {
      event.preventDefault();
      event.stopPropagation();
      inputRefs[thumbIndex].current?.focus();
      setIsDragging(true);
      setIsFocusVisible(false);
      setThumbIndex(thumbIndex);
    };

    const handleKeydownOnThumb = (event: React.KeyboardEvent, thumbIndex: number) => {
      const newValue = getKeyboardValue(
        event, value[thumbIndex], step, stepMultiplier, max, min, restrictToMarks, marks,
      );
      if (newValue === lastGestureRef.current[thumbIndex]) return;
      setIsFocusVisible(true);
      // A NEW tuple rather than an in-place write: a ref holding a mutated array is how the
      // aliasing bug above got in, and assigning a fresh one makes it impossible to repeat.
      const nextGesture: [number, number] = [...lastGestureRef.current];
      nextGesture[thumbIndex] = newValue;
      lastGestureRef.current = nextGesture;
      handleInputChange({ target: { value: newValue.toString() } }, thumbIndex);
    };

    const sharedThumbProps = {
      accessibleMaxText,
      accessibleMinText,
      "aria-label": ariaLabel,
      "aria-labelledby":
        [a11yProps?.["aria-labelledby"], ariaLabelledBy].filter(Boolean).join(" ") || undefined,
      "aria-valuemax": max,
      "aria-valuemin": min,
      "aria-valuetext": ariaValueText,
      disabled,
      format,
      max,
      min,
      restrictToMarks,
      showTooltip,
      sliderValue: value,
      step,
    };

    return (
      <SliderTrack
        disabled={disabled}
        format={format}
        handlePointerDown={handlePointerDownOnTrack}
        isDragging={isDragging}
        isRange
        marks={marks}
        max={max}
        maxLabel={maxLabel}
        min={min}
        minLabel={minLabel}
        progressPercentageRange={[startPercentage, endPercentage]}
        ref={ref}
        sliderRef={sliderRef}
        {...rest}
      >
        {[0, 1].map((i) => (
          <SliderThumb
            {...sharedThumbProps}
            handleInputChange={(event) => handleInputChange(event, i)}
            handleKeydownOnThumb={(event) => handleKeydownOnThumb(event, i)}
            handlePointerDown={(event) => handlePointerDownOnThumb(event, i)}
            index={i}
            inputRef={inputRefs[i]}
            // Only the thumb being dragged gets the dragging paint, or both would light up.
            isFocusVisible={isFocusVisible && thumbIndexState === i}
            key={i}
            offsetPercentage={`${calculatePercentage(value[i], max, min)}%`}
            onBlur={() => {
              setThumbIndex(i);
              setIsFocusVisible(false);
            }}
            onFocus={() => {
              setThumbIndex(i);
              setIsFocusVisible(true);
            }}
            trackDragging={isDragging && thumbIndexState === i}
          />
        ))}
      </SliderTrack>
    );
  },
);
